<script setup>
/** 发布工作 / 分解子任务（`?parent=<任务 id>`）：只放表单，发布后进入新任务详情。 */
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Button, Card, Input, Select, Textarea } from '@drama/ui'

import { api, currentGuild, getToken, inGuild } from '../api.js'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const parent_task_id = typeof route.query.parent === 'string' ? route.query.parent : null
const members = ref([])
const error = ref('')
const busy = ref(false)
const form = ref({ title: '', spec: '', acceptance: '', reward: 0, funding: 'personal', holder_user_id: '', client_request_key: crypto.randomUUID() })
const ownerOptions = computed(() => [{ value: '', label: t('okr.unassigned') }, ...members.value.filter((item) => item.can_assign === 1).map((item) => ({ value: item.user_id, label: item.handle }))])
const workPath = (suffix) => inGuild(`/api/voko/work${suffix}`)

onMounted(async () => {
  if (getToken() === null) {
    await router.push({ path: '/login', query: { next: route.fullPath } })
    return
  }
  if (currentGuild.slug === null) return
  members.value = (await api('GET', workPath('/members'))).members
})

async function publish() {
  busy.value = true
  error.value = ''
  try {
    const result = await api('POST', workPath('/'), { ...form.value, parent_task_id, reward: Number(form.value.reward), holder_user_id: form.value.holder_user_id === '' ? null : form.value.holder_user_id,
      acceptance: form.value.acceptance.split('\n').map((line) => line.trim()).filter((line) => line !== '') })
    await router.push(`/work/${result.task.id}`)
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('okr.title') }}</p>
      <h1 class="page-head__title">{{ parent_task_id === null ? t('okr.newTask') : t('okr.newChild') }}</h1>
    </div>
    <router-link :to="parent_task_id === null ? '/work' : `/work/${parent_task_id}`">{{ t('okr.dismiss') }}</router-link>
  </header>
  <p v-if="currentGuild.slug === null">{{ t('okr.chooseGuild') }}</p>
  <Card v-else>
    <form class="voko-form" @submit.prevent="publish">
      <label>{{ t('okr.taskTitle') }}<Input v-model="form.title" :disabled="busy" required maxlength="200" /></label>
      <label>{{ t('okr.description') }}<Textarea v-model="form.spec" :disabled="busy" /></label>
      <label>{{ t('okr.acceptance') }}<Textarea v-model="form.acceptance" :disabled="busy" /></label>
      <label>{{ t('okr.owner') }}<Select v-model="form.holder_user_id" :disabled="busy" :aria-label="t('okr.owner')" :options="ownerOptions" /></label>
      <label>{{ t('okr.reward') }}<Input v-model="form.reward" :disabled="busy" type="number" min="0" max="1000000000" /></label>
      <label v-if="Number(form.reward) > 0">{{ t('okr.funding') }}<Select v-model="form.funding" :disabled="busy" :aria-label="t('okr.funding')" :options="[{ value: 'personal', label: t('okr.personalWallet') }, { value: 'guild', label: t('okr.guildBank') }]" /></label>
      <p v-if="error" role="alert">{{ error }}</p>
      <Button type="submit" :disabled="busy || form.title.trim() === ''">{{ t('okr.publish') }}</Button>
    </form>
  </Card>
</template>

<style scoped>
.voko-form { display: grid; gap: var(--drama-space-md); }
.voko-form label { display: grid; gap: var(--drama-space-sm); }
</style>
